import React from 'react';
import { Layout, Menu, Drawer } from 'antd';
import {
  HomeOutlined,
  BarChartOutlined,
  ExperimentOutlined,
  DatabaseOutlined,
  DollarOutlined,
  ThunderboltOutlined,
  WalletOutlined,
  BulbOutlined,
  UploadOutlined,
  GlobalOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  SettingOutlined,
  LockOutlined,
  CalendarOutlined,
  RocketOutlined,
  HeatMapOutlined,
  CalculatorOutlined,
  ReadOutlined,
  AlertOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/authStore';

declare global {
  interface Window {
    __OPS_MODE__?: boolean;
  }
}

const { Sider } = Layout;

const isOpsMode = (): boolean => window.__OPS_MODE__ === true;

const menuItems = [
  {
    type: 'group' as const,
    label: '总览',
    children: [
      { key: '/dashboard', icon: <HomeOutlined />, label: '首页' },
      { key: '/stations-overview', icon: <AppstoreOutlined />, label: '场站概览' },
      { key: '/todos', icon: <UnorderedListOutlined />, label: '待办管理' },
    ],
  },
  {
    type: 'group' as const,
    label: '市场行情',
    children: [
      { key: '/public', icon: <BarChartOutlined />, label: '山东大盘' },
      { key: '/inter-province', icon: <GlobalOutlined />, label: '省间日前量价' },
      { key: '/rt-price', icon: <BulbOutlined />, label: '实时临时电价' },
      { key: '/weather-bulletin', icon: <ReadOutlined />, label: '每日气象简报' },
      { key: '/weather-forecast-map', icon: <HeatMapOutlined />, label: '新能源出力研判' },
    ],
  },
  {
    type: 'group' as const,
    label: '场站运维监控',
    children: [
      { key: '/revenue', icon: <DollarOutlined />, label: '收益测算' },
      { key: '/private', icon: <DatabaseOutlined />, label: '场站运维' },
      { key: '/agc-storage', icon: <ThunderboltOutlined />, label: '配储测算' },
      { key: '/settlement', icon: <WalletOutlined />, label: '结算单管理' },
      { key: '/inter-province-pnl', icon: <CalculatorOutlined />, label: '省间现货测算' },
      { key: '/dispatch-sop', icon: <AlertOutlined />, label: '实时调度监盘' },
    ],
  },
  {
    type: 'group' as const,
    label: '分析工具',
    children: [
      { key: '/workbench', icon: <ExperimentOutlined />, label: '交叉分析工作台' },
      { key: '/trading-diary', icon: <ReadOutlined />, label: '交易策略留痕' },
      { key: '/medium-term-trade', icon: <CalendarOutlined />, label: '中长期交易数据' },
      { key: '/medium-term-backtest', icon: <ExperimentOutlined />, label: '中长期回测' },
      { key: '/strategy-optimizer', icon: <RocketOutlined />, label: '策略寻优' },
      { key: '/optimization', icon: <RocketOutlined />, label: '申报策略指导' },
      { key: '/city-capacity-3d', icon: <HeatMapOutlined />, label: '风光装机3D地图' },
    ],
  },
];

const opsMenuItems = [
  {
    type: 'group' as const,
    label: '运维工具',
    children: [
      { key: '/dispatch-sop', icon: <AlertOutlined />, label: '实时调度监盘' },
      { key: '/private', icon: <DatabaseOutlined />, label: 'AGC实时监控' },
    ],
  },
];

interface AppSidebarProps {
  isMobile: boolean;
  mobileOpen: boolean;
  onMobileClose: () => void;
  immersive?: boolean;
}

const AppSidebar: React.FC<AppSidebarProps> = ({ isMobile, mobileOpen, onMobileClose, immersive }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarCollapsed, toggleSidebar } = useUIStore();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'admin';
  const opsMode = isOpsMode();

  const handleClick = (key: string) => {
    navigate(key);
    if (isMobile) {
      onMobileClose();
    }
  };

  const systemChildren = [
    { key: '/upload', icon: <UploadOutlined />, label: '场站数据上传' },
    { key: '/change-password', icon: <LockOutlined />, label: '修改密码' },
    ...(isAdmin ? [{ key: '/admin', icon: <SettingOutlined />, label: '用户管理' }] : []),
  ];

  const allMenuItems = opsMode
    ? [
        ...opsMenuItems,
        { type: 'group' as const, label: '系统', children: systemChildren },
      ]
    : [
        ...menuItems,
        { type: 'group' as const, label: '系统', children: systemChildren },
      ];

  const menuClassName = immersive ? 'sidebar-grouped-menu sidebar-grouped-menu--immersive' : 'sidebar-grouped-menu';

  const menuContent = (
    <Menu
      mode="inline"
      selectedKeys={[location.pathname]}
      items={allMenuItems.map((group) => ({
        type: group.type,
        label: group.label,
        children: group.children?.map((item) => ({
          key: item.key,
          icon: item.icon,
          label: item.label,
          onClick: () => handleClick(item.key),
        })),
      }))}
      style={{ borderRight: 0 }}
      className={menuClassName}
    />
  );

  const homePath = opsMode ? '/dispatch-sop' : '/dashboard';
  const appTitle = opsMode ? '⚡ 场站运维' : '⚡ 电力中台';
  const siderClassName = immersive ? 'app-sidebar app-sidebar--immersive' : 'app-sidebar';

  if (isMobile) {
    return (
      <Drawer
        placement="left"
        closable={false}
        onClose={onMobileClose}
        open={mobileOpen}
        width={220}
        rootClassName={immersive ? 'app-sidebar-drawer--immersive' : undefined}
        styles={{ body: { padding: 0 }, header: { display: 'none' } }}
      >
        <div
          className="app-sidebar__title-area"
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderBottom: immersive ? undefined : '1px solid #f0f0f0',
            cursor: 'pointer',
            flexShrink: 0,
          }}
          onClick={() => { navigate(homePath); onMobileClose(); }}
        >
          <span className="app-sidebar__title-text" style={immersive ? undefined : { fontWeight: 700, fontSize: 16, color: '#374151' }}>{appTitle}</span>
        </div>
        <div className="app-sidebar__scroll" style={{ height: 'calc(100vh - 64px)', overflowY: 'auto' }}>
          {menuContent}
        </div>
      </Drawer>
    );
  }

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={sidebarCollapsed}
      collapsedWidth={80}
      width={220}
      className={siderClassName}
      style={{
        background: immersive ? undefined : '#ffffff',
        boxShadow: immersive ? undefined : '2px 0 8px rgba(0,0,0,0.02)',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 100,
        overflow: 'hidden',
      }}
    >
      <div
        className="app-sidebar__title-area"
        style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
          padding: sidebarCollapsed ? 0 : '0 20px',
          borderBottom: immersive ? undefined : '1px solid #f0f0f0',
          cursor: 'pointer',
        }}
        onClick={() => navigate(homePath)}
      >
        {sidebarCollapsed ? (
          <span style={{ fontSize: 20 }}>⚡</span>
        ) : (
          <span className="app-sidebar__title-text" style={immersive ? undefined : { fontWeight: 700, fontSize: 16, color: '#374151', whiteSpace: 'nowrap' }}>{appTitle}</span>
        )}
      </div>
      <div className="app-sidebar__scroll" style={{ height: 'calc(100vh - 64px - 48px)', overflowY: 'auto', overflowX: 'hidden' }}>
        {menuContent}
      </div>
      <div
        className="app-sidebar__collapse-footer"
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 48,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderTop: immersive ? undefined : '1px solid #f0f0f0',
          cursor: 'pointer',
          color: immersive ? undefined : '#374151',
          background: immersive ? undefined : '#ffffff',
        }}
        onClick={toggleSidebar}
      >
        {sidebarCollapsed ? '→' : '← 收起'}
      </div>
    </Sider>
  );
};

export default AppSidebar;
